import { useState } from 'react';
import { eventBus } from '@/libs/eventBus';
import { WidgetButton } from '../widgetButton';
import { WidgetDivider } from '../widgetDivider';
import { WIDGET_COLORS } from '../../constants/colors';
import { formatPoints, formatCurrency, progressPercent } from '../../utils/formatSavingsPoints';
import type { SavingsPointsData } from '../../hooks/useGetSavingsPoints';
import { WidgetCardHeader } from '../widgetCardHeader';
import { ProgressItem } from '../progressItem';
import { StatRow } from '../statRow';
import { WidgetNotice } from '../widgetNotice';
import { InfoButton } from '../infoButton';
import { EligibilityInfoModal } from '../eligibilityInfoModal';
import { PointsBreakdownLegend } from '../pointsBreakdownLegend';
import { RateChip } from '../rateChip';

type Props = { data: SavingsPointsData };

export function BelowMinimumInvestment({ data }: Props) {
  const [infoOpen, setInfoOpen] = useState(false);
  const earned = formatPoints(data.savings_points_earned);
  const pointsPercent = progressPercent(data.savings_points_earned, data.savings_points_required);

  return (
    <>
      <WidgetCardHeader
        variant="neutral"
        badgeText="Below Minimum"
        description="Your savings are helping other families purchase their own homes while building towards purchasing your own."
      />

      <WidgetDivider />

      <ProgressItem
        label="Community Points"
        value={
          <span className="text-gray-500">
            <strong className="text-gray-800">{earned}</strong> earned so far
          </span>
        }
        barPercent={pointsPercent}
        segments={[{ flex: 1, color: WIDGET_COLORS.gray.bar }]}
        footer={
          <PointsBreakdownLegend
            basePoints={data.savings_points_earned}
            bonusPoints={data.bonus_credits_points}
            referralPoints={data.referral_credits_points}
            disabled
          />
        }
      />

      <ProgressItem
        label={
          <span className="inline-flex items-center">
            Estimated Eligibility
            <InfoButton onClick={() => setInfoOpen(true)} />
          </span>
        }
        value={<span style={{ color: WIDGET_COLORS.gray.locked }}>- Locked</span>}
        barPercent={0}
        segments={[{ flex: 1, color: WIDGET_COLORS.gray.bar }]}
      />

      <StatRow
        bg={WIDGET_COLORS.gray.statBg}
        dividerColor={WIDGET_COLORS.gray.divider}
        left={{
          value: `${earned} pts`,
          valueColor: WIDGET_COLORS.primary,
          label: 'Community Points',
          extra: <RateChip totalInvestment={data.total_investment} disabled />,
        }}
        right={{
          value: '-',
          valueColor: WIDGET_COLORS.gray.locked,
          label: (
            <span className="inline-flex items-center uppercase">
              Estimated Eligibility
              <InfoButton onClick={() => setInfoOpen(true)} />
            </span>
          ),
        }}
      />

      <WidgetNotice variant="info" icon="info">
        A minimum of <strong>£5,000</strong> invested in GYS Home is required to become eligible
        for home finance. Your current balance is{' '}
        <strong>{formatCurrency(data.total_investment)}</strong>. Top up to unlock your estimated
        eligibility date.
      </WidgetNotice>

      <WidgetDivider />

      <WidgetButton onClick={() => eventBus.emit('mife:open-top-up-modal', {})}>
        Top up your GYS Home investment
      </WidgetButton>

      <EligibilityInfoModal open={infoOpen} onOpenChange={setInfoOpen} />
    </>
  );
}
